import { Check } from "lucide-react";
import type { ProgressSection } from "../schema/formDesign.js";
import "./styles.css";

export interface StepProgressProps {
  sections: ReadonlyArray<ProgressSection>;
  /** Index into `sections` of the section the subject is on. */
  current: number;
}

/**
 * KYC-design — progress is a FIXED list of named sections. Desktop shows the rail (a tick for
 * done, a ring for current, a numbered circle for what is ahead, a note such as "If you employ
 * staff"); phones get the compact bar: current name, "Step n of N" and a track. Both are the
 * same `<StepProgress>`; CSS picks which is shown. The rail is inert — no way to jump.
 */
export function StepProgress({ sections, current }: StepProgressProps) {
  const total = sections.length;
  const position = current + 1;
  const label = `Step ${position} of ${total}`;
  const percent = total > 0 ? Math.round((position / total) * 100) : 0;
  const currentTitle = sections[current]?.title ?? "";
  return (
    <>
      <nav className="form-rail" aria-label="Form sections">
        <ol className="stepper" aria-label="Form steps">
          {sections.map((section, index) => {
            const state = index < current ? (section.skipped ? "skipped" : "done") : index === current ? "current" : "upcoming";
            return (
              <li
                key={section.key}
                className={`stepper-step stepper-step-${state}`}
                aria-current={state === "current" ? "step" : undefined}
              >
                <span className="stepper-mark" aria-hidden={state === "done" ? undefined : true}>
                  {state === "done" ? <Check size={14} role="img" aria-label="Completed" /> : index + 1}
                </span>
                <span className="stepper-titles">
                  <span className="stepper-title">{section.title}</span>
                  {section.note && state === "upcoming" && <span className="stepper-note">{section.note}</span>}
                </span>
              </li>
            );
          })}
        </ol>
      </nav>
      <div className="step-progress">
        <div className="step-progress-line">
          <span className="step-progress-current">{currentTitle}</span>
          <p className="step-progress-label">{label}</p>
        </div>
        <div
          className="step-progress-track"
          role="progressbar"
          aria-label={label}
          aria-valuemin={1}
          aria-valuemax={total}
          aria-valuenow={position}
        >
          <div className="step-progress-bar" style={{ width: `${percent}%` }} />
        </div>
      </div>
    </>
  );
}

export interface StepNavProps {
  canGoBack: boolean;
  onBack: () => void;
  isLast: boolean;
  isFirst: boolean;
  submitting: boolean;
  submitLabel: string;
  /** A note beside the primary action ("This link expires on …"). */
  note?: string;
}

/** Continue first, Back second: the primary action is the first thing the eye lands on. */
export function StepNav({ canGoBack, onBack, isLast, isFirst, submitting, submitLabel, note }: StepNavProps) {
  const primary = isLast ? submitLabel : isFirst ? "Start" : "Continue";
  return (
    <div className="step-nav">
      <button type="submit" className="button-primary" disabled={submitting} aria-disabled={submitting}>
        {primary}
      </button>
      {canGoBack && (
        <button type="button" className="button-secondary" onClick={onBack} disabled={submitting}>
          Back
        </button>
      )}
      {note && <p className="step-nav-note">{note}</p>}
    </div>
  );
}
